import * as Sailcloth from "./vendor/sailcloth/sailcloth";
export class ScoreDisplay extends Sailcloth.Renderable {
    constructor() {
        super();
        this.positionType = "static";
        this.score = 0;
        this.x = 16.5;
        this.y = 22.5;
    }
    render(viewport, sinceLastFrame) {
        viewport.context.fillStyle = "rgba(100, 100, 100, 0.80)";
        viewport.context.font = "48px Zerofour";
        viewport.context.textAlign = "left";
        viewport.context.textBaseline = "top";
        viewport.context.fillText(this.text, this.x, this.y);
    }
    get text() {
        return this.score.toString();
    }
    get score() {
        return this._score;
    }
    set score(v) {
        this._score = v;
        if (this.viewport) {
            this.viewport.refresh();
        }
    }
}
export class StopWatch extends Sailcloth.Renderable {
    constructor(options = {}) {
        super();
        this.positionType = "static";
        this.x = 16.5;
        this.y = 56.5;
        this._endDate = Date.now();
        this._isTriggered = false;
        this.options = options || {};
        this.options.onTimerEnd = this.options.onTimerEnd || function () { };
        // this timer will be cleared when the timer is removed,
        // or when the timer expires.
        //
        // as noted elsewhere, the refresh() method doesn't directly
        // render, it requests a frame, so we don't have to worry about
        // this happening too often
        this._refreshTimer = setInterval(() => {
            if (this.viewport && !this.isRenderingFinished) {
                this.viewport.refresh();
            }
        }, 500);
    }
    render(viewport, sinceLastFrame) {
        this.update(sinceLastFrame);
        viewport.context.fillStyle = this.color;
        viewport.context.font = "48px Zerofour";
        viewport.context.textAlign = "left";
        viewport.context.textBaseline = "top";
        viewport.context.fillText(this.text, this.x, this.y);
    }
    update(sinceLastFrame) {
        if (this.remainingMs <= 0 && !this._isTriggered) {
            this.options.onTimerEnd(this);
            this._isTriggered = true;
            clearTimeout(this._refreshTimer);
        }
    }
    get remainingMs() {
        return this._endDate - Date.now();
    }
    set remainingMs(ms) {
        this._endDate = Date.now() + ms;
    }
    get endDate() {
        return this._endDate;
    }
    set endDate(d) {
        this._endDate = d;
    }
    get text() {
        const seconds = Math.floor(Math.abs(this.remainingMs) / 1000);
        const minutes = Math.floor(seconds / 60);
        return `${this._pad(minutes)}:${this._pad(seconds % 60)}`;
    }
    get color() {
        if (this.remainingMs < 10 * 1000) {
            return "rgba(255, 0, 0, 0.90)";
        }
        else {
            return "rgba(68, 221, 255, 0.90)";
        }
    }
    _pad(n) {
        return n < 10 ? "0" + n.toString() : n.toString();
    }
}
export class LoadingIndicator extends Sailcloth.Renderable {
    constructor() {
        super();
        this.positionType = "static";
        this._isVisible = false;
    }
    render(viewport, sinceLastFrame) {
        if (!this._isVisible) {
            return;
        }
        const x = 32;
        const y = 32;
        const angle = ((viewport.tick % 60) / 60) * Math.PI * 2;
        viewport.context.fillStyle = "rgba(128,128,128,0.5)";
        viewport.context.beginPath();
        viewport.context.arc(x, y, 16.0, 0, Math.PI * 2);
        viewport.context.fill();
        viewport.context.fillStyle = "#4df";
        viewport.context.beginPath();
        viewport.context.arc(x, y, 16.0, angle, angle + Math.PI / 2);
        viewport.context.lineTo(x, y);
        viewport.context.fill();
    }
}
export class AlertPopup extends Sailcloth.Renderable {
    constructor(text, options = {}) {
        super();
        this.positionType = "static";
        this._text = text;
        this.options = options || {};
        this.color = this.options.color || "rgba(255, 0, 0, 0.75)";
        if (this.options.ttl) {
            setTimeout(() => {
                this.isRenderingFinished = true;
                this.viewport.refresh();
            }, this.options.ttl);
        }
    }
    render(viewport, sinceLastFrame) {
        this.x = this.x || viewport.rect.size.width / 2;
        this.y = this.y || viewport.rect.size.height / 2;
        viewport.context.fillStyle = this.color;
        viewport.context.font = "36px Zerofour";
        viewport.context.textAlign = "center";
        viewport.context.textBaseline = "middle";
        this._fittedText =
            this._fittedText ||
                this.viewport.fitText(this._text, viewport.rect.size.width);
        this.viewport.fillText(this._fittedText, this.x, this.y);
    }
}
export class FloatingAlertText extends AlertPopup {
    constructor(text, options = {}) {
        super(text, options);
        this.positionType = "world";
        this.x = this.options.x || 0;
        this.y = this.options.y || 0;
        this.yv = Math.random() * 64 - 32;
        this.xv = Math.random() * 64 - 32;
    }
    render(viewport, sinceLastFrame) {
        // better not to hardcode this, but will fix later
        if (this._text.endsWith("CR")) {
            this.color = "rgba(0,255,0,0.75)";
        }
        super.render(viewport, sinceLastFrame);
        this.update(sinceLastFrame);
    }
    update(sinceLastFrame) {
        this.x += this.xv * (sinceLastFrame / 1000);
        this.y += this.yv * (sinceLastFrame / 1000);
        this.viewport.refresh();
    }
}
const ARROW_TTL_MS = 15000;
const MAX_ARROWS = 3;
const ARROW_MARGIN = 48;
const HIT_RADIUS = 28;
// shows an arrow pointing towards the location of any tile another player
// has played recently, so you can see who else is active nearby and tap
// to jump to them. Arrows expire a while after the move that created them,
// and only the closest few are shown if several players are active at once
export class PlayerArrows extends Sailcloth.Renderable {
    constructor(board) {
        super();
        this.positionType = "static";
        this.board = board;
        this._moves = {};
        this._screenPositions = [];
        // arrows need to disappear once they expire, even if nothing else
        // happens to trigger a redraw
        setInterval(() => {
            if (this.viewport) {
                this.viewport.refresh();
            }
        }, 1000);
    }
    recordMove(username, bx, by) {
        this._moves[username] = { username, bx, by, expiresAt: Date.now() + ARROW_TTL_MS };
        if (this.viewport) {
            this.viewport.refresh();
        }
    }
    get activeMoves() {
        const now = Date.now();
        return Object.keys(this._moves)
            .map(k => this._moves[k])
            .filter(m => m.expiresAt > now);
    }
    render(viewport, sinceLastFrame) {
        const w = viewport.rect.size.width;
        const h = viewport.rect.size.height;
        const wv = viewport;
        const viewCenter = wv.center;
        const offscreen = this.activeMoves
            .map(m => {
            const p = this.board.rectForBox(m.bx, m.by).center;
            const distSq = (p.x - viewCenter.x) ** 2 + (p.y - viewCenter.y) ** 2;
            return { move: m, point: p, distSq };
        })
            // no need for an arrow pointing at a player who is already visible
            .filter(e => !wv.pointVisible(e.point.x, e.point.y))
            .sort((a, b) => a.distSq - b.distSq)
            .slice(0, MAX_ARROWS);
        this._screenPositions = [];
        offscreen.forEach(({ move, point }) => {
            const angle = Math.atan2(point.y - viewCenter.y, point.x - viewCenter.x);
            const pos = this._clampToEdge(w / 2, h / 2, angle, w, h);
            this._screenPositions.push({ x: pos.x, y: pos.y, angle, move });
            this._drawArrow(viewport, pos.x, pos.y, angle, move.username);
        });
    }
    _clampToEdge(cx, cy, angle, w, h) {
        const halfW = w / 2 - ARROW_MARGIN;
        const halfH = h / 2 - ARROW_MARGIN;
        const dirX = Math.cos(angle);
        const dirY = Math.sin(angle);
        const scale = Math.min(dirX !== 0 ? Math.abs(halfW / dirX) : Infinity, dirY !== 0 ? Math.abs(halfH / dirY) : Infinity);
        return { x: cx + dirX * scale, y: cy + dirY * scale };
    }
    _drawArrow(viewport, x, y, angle, username) {
        const ctx = viewport.context;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.fillStyle = "#000";
        ctx.beginPath();
        ctx.moveTo(14, 0);
        ctx.lineTo(-8, -8);
        ctx.lineTo(-8, 8);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#fd4";
        ctx.beginPath();
        ctx.moveTo(12, 0);
        ctx.lineTo(-4, -5);
        ctx.lineTo(-4, 5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.save();
        ctx.fillStyle = "#222";
        ctx.font = "16px Zerofour";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(username, x - Math.cos(angle) * 22, y - Math.sin(angle) * 22);
        ctx.restore();
    }
    // finds the arrow (if any) under the given screen-space point; x/y here
    // are already canvas-local coordinates
    _hitTestScreen(x, y) {
        const hit = this._screenPositions.find(p => {
            const dx = p.x - x;
            const dy = p.y - y;
            return Math.sqrt(dx * dx + dy * dy) <= HIT_RADIUS;
        });
        return hit ? hit.move : null;
    }
    // clientX/clientY are raw window coordinates, eg. Hammer's e.center.x/y
    // or a mouse event's clientX/clientY
    _toCanvasLocal(clientX, clientY) {
        const canvas = this.viewport.canvas;
        return { x: clientX - canvas.offsetLeft, y: clientY - canvas.offsetTop };
    }
    // used to show a pointer cursor while hovering an arrow with a mouse
    isPointOverArrow(clientX, clientY) {
        if (!this.viewport) {
            return false;
        }
        const p = this._toCanvasLocal(clientX, clientY);
        return this._hitTestScreen(p.x, p.y) != null;
    }
    handleTap(clientX, clientY) {
        if (!this.viewport) {
            return false;
        }
        const p = this._toCanvasLocal(clientX, clientY);
        const move = this._hitTestScreen(p.x, p.y);
        if (!move) {
            return false;
        }
        const target = this.board.rectForBox(move.bx, move.by).center;
        this.viewport.setCenter(target.x, target.y);
        return true;
    }
}
